import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FONT_FAMILY_OPTIONS,
  MAX_COMPANY_NAME_LENGTH,
  MAX_HERO_BACKGROUNDS,
  MAX_HERO_SUPPORTING_TEXT_LENGTH,
  MAX_HERO_TITLE_LENGTH,
  MAX_IMAGE_DATA_URL_LENGTH,
  MAX_IMAGE_UPLOAD_SIZE_BYTES,
  MAX_QUICK_LINKS,
} from "../config";
import Header from "./Header";
import Hero from "./Hero";
import PropertyIntro from "./PropertyIntro";
import PropertySlider from "./PropertySlider";
import Properties from "./Properties";
import Footer from "./Footer";
import "../css/vitrine-config.css";

const previewSectionComponents = {
  hero: Hero,
  "featured-properties": PropertySlider,
  "property-intro": PropertyIntro,
};

const VitrineLivePreview = ({
  themeColor,
  secondaryColor,
  fontFamily,
  logoUrl,
  companyName,
  headerItems,
  heroBackgrounds,
  quickLinks,
  sections,
  showMore,
  propertiesColumns,
  onPropertiesColumnsChange,
  selectedPage,
  onSelectPage,
}) => (
  <aside className="vitrine-config-live-preview">
    <div className="vitrine-config-live-preview-heading">
      <div
        className="vitrine-config-preview-tabs"
        role="tablist"
        aria-label="Página para pré-visualizar"
      >
        <button
          type="button"
          role="tab"
          aria-selected={selectedPage === "home"}
          className={selectedPage === "home" ? "is-active" : ""}
          onClick={() => onSelectPage("home")}
        >
          Vitrine
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={selectedPage === "properties"}
          className={selectedPage === "properties" ? "is-active" : ""}
          onClick={() => onSelectPage("properties")}
        >
          Imóveis
        </button>
      </div>
    </div>
    <div className="vitrine-config-preview-window">
      <div
        className="vitrine-config-preview-site"
        onClickCapture={(event) => {
          if (event.target.closest("a[href]")) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onSubmitCapture={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        style={{
          "--vitrine-theme-color": themeColor,
          "--vitrine-secondary-color": secondaryColor,
          "--vitrine-font-family": FONT_FAMILY_OPTIONS[fontFamily],
        }}
      >
        <Header
          headerItems={headerItems}
          logoUrl={logoUrl}
          companyName={companyName}
        />
        {selectedPage === "home" ? (
          <>
            {sections
              .filter((section) => section.visible !== false)
              .map((section) => {
                const Section = previewSectionComponents[section.id];
                return (
                  <div
                    className="vitrine-config-preview-section"
                    key={section.id}
                    style={{
                      "--vitrine-theme-color":
                        section.themeColor || themeColor,
                      ...(section.backgroundColor && {
                        "--vitrine-section-background-color":
                          section.backgroundColor,
                      }),
                    }}
                  >
                    <Section
                      title={section.title}
                      supportingText={section.supportingText}
                      quickLinks={quickLinks}
                      backgroundImages={heroBackgrounds}
                    />
                  </div>
                );
              })}
            <Footer showMore={showMore} />
          </>
        ) : (
          <Properties
            cardsPerRow={propertiesColumns}
            onCardsPerRowChange={onPropertiesColumnsChange}
            isPreview
          />
        )}
      </div>
    </div>
  </aside>
);

const supportedImageTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const isImageDataUrl = (value) => value.startsWith("data:image/");

const ImageDropzone = ({ id, onFile, disabled = false, imageSource }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled) {
      return;
    }

    const [file] = event.dataTransfer.files;
    if (file) {
      onFile(file);
    }
  };

  return (
    <div
      className={`vitrine-config-image-dropzone${
        isDragging ? " is-dragging" : ""
      }`}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) {
          setIsDragging(true);
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) {
          event.dataTransfer.dropEffect = "copy";
          setIsDragging(true);
        }
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsDragging(false);
        }
      }}
      onDrop={handleDrop}
    >
      <input
        id={id}
        className="vitrine-config-image-dropzone-input"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        disabled={disabled}
        onChange={(event) => {
          const [file] = event.target.files;
          if (file) {
            onFile(file);
          }
          event.target.value = "";
        }}
      />
      <label
        className="vitrine-config-image-dropzone-label"
        htmlFor={id}
      >
        {imageSource && (
          <img
            className="vitrine-config-image-dropzone-preview"
            src={imageSource}
            alt="Pré-visualização da imagem selecionada"
          />
        )}
        <span className="vitrine-config-image-dropzone-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v5h14v-5" />
          </svg>
        </span>
        <span className="vitrine-config-image-dropzone-title">
          {isDragging ? "Solte a imagem aqui" : "Arraste uma imagem até aqui"}
        </span>
        <span className="vitrine-config-image-dropzone-alternative">ou</span>
        <span className="vitrine-config-image-dropzone-button">
          Escolher arquivo
        </span>
        <span className="vitrine-config-image-dropzone-hint">
          PNG, JPEG, WebP ou GIF · até 512 KB
        </span>
      </label>
    </div>
  );
};

const readImageFile = (file) =>
  new Promise((resolve, reject) => {
    if (!supportedImageTypes.has(file.type)) {
      reject(new Error("Use uma imagem PNG, JPEG, WebP ou GIF."));
      return;
    }

    if (file.size === 0 || file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
      reject(new Error("O arquivo deve ter no máximo 512 KB."));
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result !== "string") {
        reject(new Error("O arquivo selecionado não pôde ser lido."));
        return;
      }

      resolve(reader.result);
    });
    reader.addEventListener("error", () => {
      reject(reader.error || new Error("O arquivo selecionado não pôde ser lido."));
    });
    reader.addEventListener("abort", () => {
      reject(new Error("A leitura do arquivo foi cancelada."));
    });
    reader.readAsDataURL(file);
  });

const sectionNames = {
  hero: "Hero",
  "featured-properties": "Imóveis em destaque",
  "property-intro": "Apresentação dos imóveis",
};

const headerItemNames = {
  mcmv: "Imóveis MCMV",
  planta: "Imóveis na Planta",
  novos: "Imóveis Novos",
  sell: "Venda seu Imóvel",
};

const supportingTextLabels = {
  hero: "Texto de apoio",
  "featured-properties": "Texto acima do título",
  "property-intro": "Descrição",
};

const defaultSectionBackgrounds = {
  "featured-properties": "#ffffff",
  "property-intro": "#f8f9fa",
};

const VitrineConfig = ({
  config,
  initialError,
  onSave,
  onReset,
}) => {
  const [sections, setSections] = useState(config.sections);
  const [headerItems, setHeaderItems] = useState(config.headerItems);
  const [logoUrl, setLogoUrl] = useState(config.logoUrl);
  const [companyName, setCompanyName] = useState(config.companyName);
  const [heroBackgrounds, setHeroBackgrounds] = useState(
    config.heroBackgrounds,
  );
  const [quickLinks, setQuickLinks] = useState(config.quickLinks);
  const [themeColor, setThemeColor] = useState(config.themeColor);
  const [secondaryColor, setSecondaryColor] = useState(config.secondaryColor);
  const [fontFamily, setFontFamily] = useState(config.fontFamily);
  const [showMore, setShowMore] = useState(config.showMore);
  const [propertiesColumns, setPropertiesColumns] = useState(
    config.propertiesColumns,
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState(initialError);
  const [previewPage, setPreviewPage] = useState("home");

  const handleImageUpload = async (file, onLoad) => {
    try {
      const imageDataUrl = await readImageFile(file);
      onLoad(imageDataUrl);
      setError("");
      setMessage("");
    } catch (uploadError) {
      setError(`Não foi possível carregar a imagem: ${uploadError.message}`);
      setMessage("");
    }
  };

  const updateTitle = (id, title) => {
    setSections((currentSections) =>
      currentSections.map((section) =>
        section.id === id ? { ...section, title } : section,
      ),
    );
    setMessage("");
  };

  const updateVisibility = (id, visible) => {
    setSections((currentSections) =>
      currentSections.map((section) =>
        section.id === id ? { ...section, visible } : section,
      ),
    );
    setMessage("");
  };

  const updateSection = (id, changes) => {
    setSections((currentSections) =>
      currentSections.map((section) =>
        section.id === id ? { ...section, ...changes } : section,
      ),
    );
    setMessage("");
  };

  const updateQuickLink = (index, changes) => {
    setQuickLinks((currentLinks) =>
      currentLinks.map((link, linkIndex) =>
        linkIndex === index ? { ...link, ...changes } : link,
      ),
    );
    setMessage("");
  };

  const updateHeaderItemVisibility = (id, visible) => {
    setHeaderItems((currentItems) =>
      currentItems.map((item) =>
        item.id === id ? { ...item, visible } : item,
      ),
    );
    setMessage("");
  };

  const moveHeaderItem = (index, direction) => {
    setHeaderItems((currentItems) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= currentItems.length) {
        return currentItems;
      }

      const reorderedItems = [...currentItems];
      [reorderedItems[index], reorderedItems[targetIndex]] = [
        reorderedItems[targetIndex],
        reorderedItems[index],
      ];
      return reorderedItems;
    });
    setMessage("");
  };

  const moveSection = (index, direction) => {
    setSections((currentSections) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= currentSections.length) {
        return currentSections;
      }

      const reorderedSections = [...currentSections];
      [reorderedSections[index], reorderedSections[targetIndex]] = [
        reorderedSections[targetIndex],
        reorderedSections[index],
      ];
      return reorderedSections;
    });
    setMessage("");
  };

  const handleSave = (event) => {
    event.preventDefault();
    try {
      onSave({
        version: 1,
        themeColor,
        secondaryColor,
        fontFamily,
        showMore,
        propertiesColumns,
        logoUrl,
        companyName,
        heroBackgrounds,
        headerItems,
        quickLinks,
        sections,
      });
      setError("");
      setMessage("Configuração salva neste navegador.");
    } catch (saveError) {
      setError(`Não foi possível salvar a configuração: ${saveError.message}`);
      setMessage("");
    }
  };

  const handleReset = () => {
    try {
      const defaultConfig = onReset();
      setSections(defaultConfig.sections);
      setHeaderItems(defaultConfig.headerItems);
      setLogoUrl(defaultConfig.logoUrl);
      setCompanyName(defaultConfig.companyName);
      setHeroBackgrounds(defaultConfig.heroBackgrounds);
      setQuickLinks(defaultConfig.quickLinks);
      setThemeColor(defaultConfig.themeColor);
      setSecondaryColor(defaultConfig.secondaryColor);
      setFontFamily(defaultConfig.fontFamily);
      setShowMore(defaultConfig.showMore);
      setPropertiesColumns(defaultConfig.propertiesColumns);
      setError("");
      setMessage("Configuração padrão restaurada.");
    } catch (resetError) {
      setError(`Não foi possível restaurar a configuração: ${resetError.message}`);
      setMessage("");
    }
  };

  return (
    <main className="vitrine-config">
      <div className="vitrine-config-header">
        <div>
          <p className="vitrine-config-eyebrow">Personalização</p>
          <h1>Configurar vitrine</h1>
          <p>
            Personalize textos, cores, visibilidade e ordem das seções da
            página inicial.
          </p>
        </div>
        <Link className="vitrine-config-preview" to="/">
          Ver vitrine
        </Link>
      </div>

      <div className="vitrine-config-workspace">
      <div className="vitrine-config-editor">
      <form onSubmit={handleSave}>
        <section
          className="vitrine-config-theme"
          aria-labelledby="theme-color-label"
        >
          <div>
            <h2 id="theme-color-label">Cor do tema</h2>
            <p>Escolha a cor principal dos destaques e botões da vitrine.</p>
          </div>
          <label className="vitrine-config-color-control">
            <span className="visually-hidden">Selecionar cor do tema</span>
            <input
              type="color"
              value={themeColor}
              aria-label="Selecionar cor do tema"
              onChange={(event) => {
                setThemeColor(event.target.value);
                setMessage("");
              }}
            />
            <span>{themeColor.toUpperCase()}</span>
          </label>
        </section>

        <section
          className="vitrine-config-theme vitrine-config-identity"
          aria-labelledby="secondary-color-label"
        >
          <div className="vitrine-config-identity-fields">
            <div className="vitrine-config-identity-color">
              <h2 id="secondary-color-label">Cor secundária</h2>
              <p>Cor complementar para detalhes e destaques da vitrine.</p>
              <label className="vitrine-config-color-control">
                <span className="visually-hidden">
                  Selecionar cor secundária
                </span>
                <input
                  type="color"
                  value={secondaryColor}
                  aria-label="Selecionar cor secundária"
                  onChange={(event) => {
                    setSecondaryColor(event.target.value);
                    setMessage("");
                  }}
                />
                <span>{secondaryColor.toUpperCase()}</span>
              </label>
            </div>
            <label className="vitrine-config-font-control">
              <span>Tipografia</span>
              <select
                value={fontFamily}
                onChange={(event) => {
                  setFontFamily(event.target.value);
                  setMessage("");
                }}
              >
                <option value="work-sans">Work Sans</option>
                <option value="arial">Arial</option>
                <option value="georgia">Georgia</option>
                <option value="trebuchet-ms">Trebuchet MS</option>
              </select>
            </label>
          </div>
        </section>

        <section
          className="vitrine-config-theme vitrine-config-footer-option"
          aria-labelledby="show-more-label"
        >
          <div>
            <h2 id="show-more-label">Saiba Mais</h2>
            <p>Exiba ou oculte esta chamada no rodapé da página inicial.</p>
          </div>
          <label className="vitrine-config-visibility">
            <input
              type="checkbox"
              checked={showMore}
              onChange={(event) => {
                setShowMore(event.target.checked);
                setMessage("");
              }}
            />
            <span>Exibir na vitrine</span>
          </label>
        </section>

        <section
          className="vitrine-config-theme vitrine-config-properties-columns"
          aria-labelledby="properties-columns-label"
        >
          <div>
            <h2 id="properties-columns-label">Cartões na tela de imóveis</h2>
            <p>
              Define o máximo em telas grandes; em telas menores a quantidade
              se ajusta automaticamente.
            </p>
          </div>
          <label className="vitrine-config-columns-control">
            <span>Cartões por linha</span>
            <select
              value={propertiesColumns}
              onChange={(event) => {
                setPropertiesColumns(Number(event.target.value));
                setMessage("");
              }}
            >
              {[1, 2, 3, 4].map((columns) => (
                <option value={columns} key={columns}>
                  {columns}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section
          className="vitrine-config-header-items"
          aria-labelledby="header-items-title"
        >
          <header className="vitrine-config-header-items-heading">
            <div>
              <h2 id="header-items-title">Itens do header</h2>
              <p>
                Personalize a logo e o nome da imobiliária, escolha quais itens
                aparecem e ajuste a ordem do menu.
              </p>
            </div>
          </header>
          <label htmlFor="header-logo-url">URL da logo</label>
          <input
            id="header-logo-url"
            className="vitrine-config-header-logo-url"
            type="text"
            value={isImageDataUrl(logoUrl) ? "" : logoUrl}
            maxLength={
              isImageDataUrl(logoUrl) ? MAX_IMAGE_DATA_URL_LENGTH : 2048
            }
            placeholder="https://exemplo.com/logo.png"
            required={!isImageDataUrl(logoUrl)}
            onChange={(event) => {
              setLogoUrl(event.target.value);
              setMessage("");
            }}
          />
          <ImageDropzone
            id="header-logo-upload"
            onFile={(file) => handleImageUpload(file, setLogoUrl)}
            imageSource={logoUrl}
          />
          {isImageDataUrl(logoUrl) && (
            <p className="vitrine-config-image-upload-note">
              Imagem enviada e salva junto à configuração deste navegador.
            </p>
          )}
          <label htmlFor="header-company-name">Nome da imobiliária</label>
          <input
            id="header-company-name"
            className="vitrine-config-header-logo-url"
            type="text"
            value={companyName}
            maxLength={MAX_COMPANY_NAME_LENGTH}
            placeholder="Deixe em branco para ocultar"
            onChange={(event) => {
              setCompanyName(event.target.value);
              setMessage("");
            }}
          />
          <p className="vitrine-config-header-company-note">
            O nome aparece ao lado da logo em telas grandes e fica oculto em
            telas menores.
          </p>
          <ol className="vitrine-config-list">
            {headerItems.map((item, index) => (
              <li
                className={`vitrine-config-item vitrine-config-header-item${
                  item.visible === false ? " is-hidden" : ""
                }`}
                key={item.id}
              >
                <div className="vitrine-config-order">
                  <span>{headerItemNames[item.id]}</span>
                  <div>
                    <button
                      type="button"
                      aria-label={`Mover ${headerItemNames[item.id]} para cima`}
                      disabled={index === 0}
                      onClick={() => moveHeaderItem(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Mover ${headerItemNames[item.id]} para baixo`}
                      disabled={index === headerItems.length - 1}
                      onClick={() => moveHeaderItem(index, 1)}
                    >
                      ↓
                    </button>
                  </div>
                </div>
                <label className="vitrine-config-visibility">
                  <input
                    type="checkbox"
                    checked={item.visible}
                    onChange={(event) =>
                      updateHeaderItemVisibility(item.id, event.target.checked)
                    }
                  />
                  <span>Exibir no header</span>
                </label>
              </li>
            ))}
          </ol>
        </section>

        <ol className="vitrine-config-list">
          {sections.map((section, index) => (
            <li
              className={`vitrine-config-item${
                section.visible === false ? " is-hidden" : ""
              }`}
              key={section.id}
            >
              <div className="vitrine-config-order">
                <span>{sectionNames[section.id]}</span>
                <div>
                  <button
                    type="button"
                    aria-label={`Mover ${sectionNames[section.id]} para cima`}
                    disabled={index === 0}
                    onClick={() => moveSection(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Mover ${sectionNames[section.id]} para baixo`}
                    disabled={index === sections.length - 1}
                    onClick={() => moveSection(index, 1)}
                  >
                    ↓
                  </button>
                </div>
              </div>
              <label htmlFor={`section-title-${section.id}`}>
                Título da seção
              </label>
              <input
                id={`section-title-${section.id}`}
                type="text"
                value={section.title}
                maxLength={
                  section.id === "hero" ? MAX_HERO_TITLE_LENGTH : 100
                }
                required
                disabled={section.visible === false}
                onChange={(event) =>
                  updateTitle(section.id, event.target.value)
                }
              />
              <label htmlFor={`section-supporting-text-${section.id}`}>
                {supportingTextLabels[section.id]}
              </label>
              <textarea
                id={`section-supporting-text-${section.id}`}
                value={section.supportingText}
                maxLength={
                  section.id === "hero"
                    ? MAX_HERO_SUPPORTING_TEXT_LENGTH
                    : 250
                }
                rows={3}
                disabled={section.visible === false}
                onChange={(event) =>
                  updateSection(section.id, {
                    supportingText: event.target.value,
                  })
                }
              />
              {section.id === "hero" && (
                <>
                <details
                  className="vitrine-config-quick-links"
                >
                  <summary className="vitrine-config-collapsible-summary">
                    <span>Botões de filtros</span>
                    <span>
                      Personalize o título e a URL de cada botão. Você pode
                      configurar até {MAX_QUICK_LINKS} botões.
                    </span>
                  </summary>
                  <ol className="vitrine-config-list">
                    {quickLinks.map((quickLink, linkIndex) => (
                      <li
                        className="vitrine-config-item vitrine-config-quick-link"
                        key={linkIndex}
                      >
                        <div className="vitrine-config-order">
                          <span>Botão {linkIndex + 1}</span>
                          <button
                            className="vitrine-config-remove-link"
                            type="button"
                            aria-label={`Remover botão ${linkIndex + 1}`}
                            disabled={section.visible === false}
                            onClick={() => {
                              setQuickLinks((currentLinks) =>
                                currentLinks.filter(
                                  (_, currentIndex) =>
                                    currentIndex !== linkIndex,
                                ),
                              );
                              setMessage("");
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <label htmlFor={`quick-link-title-${linkIndex}`}>
                          Título
                        </label>
                        <input
                          id={`quick-link-title-${linkIndex}`}
                          type="text"
                          value={quickLink.title}
                          maxLength={60}
                          required
                          disabled={section.visible === false}
                          onChange={(event) =>
                            updateQuickLink(linkIndex, {
                              title: event.target.value,
                            })
                          }
                        />
                        <label htmlFor={`quick-link-url-${linkIndex}`}>
                          URL
                        </label>
                        <input
                          id={`quick-link-url-${linkIndex}`}
                          type="text"
                          value={quickLink.url}
                          maxLength={2048}
                          placeholder="/properties?city=esteio"
                          required
                          disabled={section.visible === false}
                          onChange={(event) =>
                            updateQuickLink(linkIndex, {
                              url: event.target.value,
                            })
                          }
                        />
                      </li>
                    ))}
                  </ol>
                  {quickLinks.length < MAX_QUICK_LINKS && (
                    <button
                      className="vitrine-config-add-link"
                      type="button"
                      disabled={section.visible === false}
                      onClick={() => {
                        setQuickLinks((currentLinks) => [
                          ...currentLinks,
                          { title: "", url: "" },
                        ]);
                        setMessage("");
                      }}
                    >
                      Adicionar botão
                    </button>
                  )}
                </details>
                <details className="vitrine-config-hero-backgrounds">
                  <summary className="vitrine-config-collapsible-summary">
                    <span>Imagens de fundo</span>
                    <span>
                      Adicione até {MAX_HERO_BACKGROUNDS} imagens por URL ou
                      arquivo. Uma imagem será escolhida aleatoriamente ao
                      carregar a página.
                    </span>
                  </summary>
                    <ol className="vitrine-config-list">
                      {heroBackgrounds.map((imageUrl, imageIndex) => (
                        <li
                          className="vitrine-config-item vitrine-config-quick-link"
                          key={imageIndex}
                        >
                          <div className="vitrine-config-order">
                            <span>Imagem {imageIndex + 1}</span>
                            <button
                              className="vitrine-config-remove-link"
                              type="button"
                              aria-label={`Remover imagem ${imageIndex + 1}`}
                              disabled={section.visible === false}
                              onClick={() => {
                                setHeroBackgrounds((currentImages) =>
                                  currentImages.filter(
                                    (_, currentIndex) =>
                                      currentIndex !== imageIndex,
                                  ),
                                );
                                setMessage("");
                              }}
                            >
                              ×
                            </button>
                          </div>
                          <label htmlFor={`hero-background-${imageIndex}`}>
                            URL da imagem
                          </label>
                          <input
                            id={`hero-background-${imageIndex}`}
                            type="text"
                            value={isImageDataUrl(imageUrl) ? "" : imageUrl}
                            maxLength={
                              isImageDataUrl(imageUrl)
                                ? MAX_IMAGE_DATA_URL_LENGTH
                                : 2048
                            }
                            placeholder="https://exemplo.com/imagem.jpg"
                            required={!isImageDataUrl(imageUrl)}
                            disabled={section.visible === false}
                            onChange={(event) => {
                              const value = event.target.value;
                              setHeroBackgrounds((currentImages) =>
                                currentImages.map((currentUrl, currentIndex) =>
                                  currentIndex === imageIndex
                                    ? value
                                    : currentUrl,
                                ),
                              );
                              setMessage("");
                            }}
                          />
                          <ImageDropzone
                            id={`hero-background-upload-${imageIndex}`}
                            disabled={section.visible === false}
                            imageSource={imageUrl}
                            onFile={(file) =>
                              handleImageUpload(file, (imageDataUrl) => {
                                setHeroBackgrounds((currentImages) =>
                                  currentImages.map(
                                    (currentUrl, currentIndex) =>
                                      currentIndex === imageIndex
                                        ? imageDataUrl
                                        : currentUrl,
                                  ),
                                );
                              })
                            }
                          />
                          {isImageDataUrl(imageUrl) && (
                            <p className="vitrine-config-image-upload-note">
                              Imagem enviada e salva junto à configuração deste
                              navegador.
                            </p>
                          )}
                        </li>
                      ))}
                    </ol>
                    {heroBackgrounds.length < MAX_HERO_BACKGROUNDS && (
                      <button
                        className="vitrine-config-add-link"
                        type="button"
                        disabled={section.visible === false}
                        onClick={() => {
                          setHeroBackgrounds((currentImages) => [
                            ...currentImages,
                            "",
                          ]);
                          setMessage("");
                        }}
                      >
                        Adicionar imagem
                      </button>
                    )}
                </details>
                </>
              )}
              <label
                className="vitrine-config-section-color-label"
                htmlFor={`section-theme-color-${section.id}`}
              >
                Cor do componente
              </label>
              <div className="vitrine-config-section-color">
                <input
                  id={`section-theme-color-${section.id}`}
                  type="color"
                  value={section.themeColor || themeColor}
                  aria-label={`Cor do componente ${sectionNames[section.id]}`}
                  disabled={section.visible === false}
                  onChange={(event) =>
                    updateSection(section.id, {
                      themeColor: event.target.value,
                    })
                  }
                />
                <span>
                  {(section.themeColor || themeColor).toUpperCase()}
                </span>
                {section.themeColor && (
                  <button
                    type="button"
                    className="vitrine-config-inherit-color"
                    disabled={section.visible === false}
                    onClick={() =>
                      updateSection(section.id, { themeColor: null })
                    }
                  >
                    Usar cor geral
                  </button>
                )}
              </div>
              {section.id !== "hero" && (
                <>
                  <label
                    className="vitrine-config-section-color-label"
                    htmlFor={`section-background-color-${section.id}`}
                  >
                    Cor de fundo da seção
                  </label>
                  <div className="vitrine-config-section-color">
                    <input
                      id={`section-background-color-${section.id}`}
                      type="color"
                      value={
                        section.backgroundColor ||
                        defaultSectionBackgrounds[section.id]
                      }
                      aria-label={`Cor de fundo ${sectionNames[section.id]}`}
                      disabled={section.visible === false}
                      onChange={(event) =>
                        updateSection(section.id, {
                          backgroundColor: event.target.value,
                        })
                      }
                    />
                    <span>
                      {(
                        section.backgroundColor ||
                        defaultSectionBackgrounds[section.id]
                      ).toUpperCase()}
                    </span>
                    {section.backgroundColor && (
                      <button
                        type="button"
                        className="vitrine-config-inherit-color"
                        disabled={section.visible === false}
                        onClick={() =>
                          updateSection(section.id, { backgroundColor: null })
                        }
                      >
                        Usar cor padrão
                      </button>
                    )}
                  </div>
                </>
              )}
              <label className="vitrine-config-visibility">
                <input
                  type="checkbox"
                  checked={section.visible !== false}
                  onChange={(event) =>
                    updateVisibility(section.id, event.target.checked)
                  }
                />
                <span>Exibir na vitrine</span>
              </label>
            </li>
          ))}
        </ol>

        {error && (
          <p className="vitrine-config-error" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="vitrine-config-success" role="status">
            {message}
          </p>
        )}

        <div className="vitrine-config-actions">
          <button className="vitrine-config-save" type="submit">
            Salvar configuração
          </button>
          <button
            className="vitrine-config-reset"
            type="button"
            onClick={handleReset}
          >
            Restaurar padrão
          </button>
        </div>
      </form>
      <p className="vitrine-config-note">
        Nesta versão, as alterações ficam salvas somente neste navegador.
      </p>
      </div>
      <VitrineLivePreview
        themeColor={themeColor}
        secondaryColor={secondaryColor}
        fontFamily={fontFamily}
        logoUrl={logoUrl}
        companyName={companyName}
        headerItems={headerItems}
        heroBackgrounds={heroBackgrounds}
        quickLinks={quickLinks}
        sections={sections}
        showMore={showMore}
        propertiesColumns={propertiesColumns}
        onPropertiesColumnsChange={setPropertiesColumns}
        selectedPage={previewPage}
        onSelectPage={setPreviewPage}
      />
      </div>
    </main>
  );
};

export default VitrineConfig;
